import {
  AbstractControl,
  Validators,
  ValidatorFn,
  FormGroup,
  ValidationErrors,
  FormControl
} from '@angular/forms';
import { DateUtil } from './date.util';
import { LogService } from '../log/log.service';
import { Injectable, Injector } from '@angular/core';
import { isNullOrUndefined } from 'util';
import { Constants } from '../constants';
import { PaymentType } from 'app/event/models/payment-type';
import { DecimalPipe } from '@angular/common';
import { PartyType } from 'app/event/models/party-type';

@Injectable()
export class PioneerValidators {
  static LOG_SERVICE: LogService;
  static injector: Injector;

  static isInteger(formControl: AbstractControl): { [key: string]: boolean } | null {
    if (!formControl.value) {
      return null;
    }

    if (!Number.isInteger(formControl.value)) {
      return { 'not_integer': true };
    }

    return null;
  }

  static isValidAmount(formControl: AbstractControl): { [key: string]: boolean } | null {
    // get the failure message from Validators.required instead
    if (Validators.required(formControl)) {
      return null;
    }

    // regular expression to match 1 to 13 digits, optionally followed by a decimal and up to 2 more digits
    const format = /^\d{1,13}(\.\d\d?)?$/;

    if (!format.test(formControl.value)) {
      return { 'invalid_amount': true };
    }

    return null;
  }

  static isValidAmountInclNegative(formControl: AbstractControl): { [key: string]: boolean } | null {
    // get the failure message from Validators.required instead
    if (Validators.required(formControl)) {
      return null;
    }

    // regular expression to match 1 to 13 digits, optionally followed by a decimal and up to 2 more digits
    const format = /^-?\d{1,13}(\.\d\d?)?$/;

    if (!format.test(formControl.value)) {
      return { 'invalid_amount': true };
    }

    return null;
  }

  static isDate(formControl: AbstractControl): { [key: string]: boolean } | null {
    if (!formControl.value) {
      return null;
    }

    let isValid = false;
    try {
      let dateWrapper = DateUtil.parseDate(formControl.value);
      isValid = !isNaN(dateWrapper.getDate());
    } catch (error) {
      PioneerValidators.LOG_SERVICE.error(error);
      isValid = false;
    }

    if (!isValid) {
      return { 'date': true };
    }

    return null;
  }

  static isEndDateAfterStartDateCrossValidator(startDateControlName: string, endDateControlName): ValidatorFn {

    return (formGroup: FormGroup): ValidationErrors | null => {
      let validationErrors: ValidationErrors = null;
      try {
        let startDate = PioneerValidators.parseDate(formGroup.get(startDateControlName).value);
        let endDate = PioneerValidators.parseDate(formGroup.get(endDateControlName).value);
        if (!!startDate && !!endDate) {
            if (endDate.getTime() <= startDate.getTime()) {
              validationErrors = { 'end_date_after_start_date': true };
              formGroup.get(endDateControlName).markAsTouched();
            }
        }
      } catch (error) {
        PioneerValidators.LOG_SERVICE.error(error);
      }
      return validationErrors;
    };

  }

  static isFirstNameOrLastNameRequiredCrossValidator(firstNameControlName: string, lastNameControlName: string): ValidatorFn {

    return (formGroup: FormGroup): ValidationErrors | null => {
      let validationErrors: ValidationErrors = null;
      try {
        let firstName = formGroup.get(firstNameControlName).value;
        let lastName = formGroup.get(lastNameControlName).value;
        if ((isNullOrUndefined(firstName) || firstName.trim().length === 0)
          && (isNullOrUndefined(lastName) || lastName.trim().length === 0)) {
          validationErrors = { 'first_name_or_last_name_required': true };
        }
      } catch (error) {
        PioneerValidators.LOG_SERVICE.error(error);
      }
      return validationErrors;
    };

  }

  static isAmountEqualToSpendAmountByCommRate(amountControlName: string,
                                              spendAmountControlName: string,
                                              commRateControlName: string): ValidatorFn {

    return (formGroup: FormGroup): ValidationErrors | null => {
      let validationErrors: ValidationErrors = null;
      try {
        const controlErrors: ValidationErrors = formGroup.get(amountControlName).errors;
        if (controlErrors != null) {
          return validationErrors;
        }
        let amount = formGroup.get(amountControlName).value;
        let spendAmount = formGroup.get(spendAmountControlName).value;
        let commRate = formGroup.get(commRateControlName).value;
        if (spendAmount != null || commRate != null) {
          if (spendAmount == null) {
            spendAmount = 0;
          }
          if (commRate == null) {
            commRate = 0;
          }
        }
        if (amount != null && spendAmount != null && commRate != null) {
          const formula = Number(((spendAmount * commRate) / 100).toFixed(2));
          if (amount !== formula) {
            validationErrors = { 'amount_must_equal_spend_x_comm_rate': true };
            formGroup.get(amountControlName).markAsTouched();
          }
        }
      } catch (error) {
        PioneerValidators.LOG_SERVICE.error(error);
      }
      return validationErrors;
    };

  }

  static isValidAmountInRange(minAmount?: number, maxAmount?: number): ValidatorFn {
    return (formControl: AbstractControl): { [key: string]: boolean } | null => {

      const validationCurrency = PioneerValidators.isValidAmount(formControl);
      if (validationCurrency !== null) {
        return validationCurrency;
      }
      if (minAmount) {
        const validationMin = Validators.min(minAmount).call(null, formControl);
        if (validationMin !== null) {
          return validationMin;
        }
      }
      if (maxAmount) {
        const validationMax = Validators.max(maxAmount).call(null, formControl);
        if (validationMax !== null) {
          return validationMax;
        }
      }
      return null;
    };
  }

  static isValidAmountInRangeInclNegative(minAmount?: number, maxAmount?: number): ValidatorFn {
    return (formControl: AbstractControl): { [key: string]: boolean } | null => {

      const validationCurrency = PioneerValidators.isValidAmountInclNegative(formControl);
      if (validationCurrency !== null) {
        return validationCurrency;
      }
      if (minAmount) {
        const validationMin = Validators.min(minAmount).call(null, formControl);
        if (validationMin !== null) {
          return validationMin;
        }
      }
      if (maxAmount) {
        const validationMax = Validators.max(maxAmount).call(null, formControl);
        if (validationMax !== null) {
          return validationMax;
        }
      }
      return null;
    };
  }

  static isValidEmail(formControl: AbstractControl): { [key: string]: boolean } | null {
    if (Validators.required(formControl)) {
      // get the failure message from Validators.required instead
      return null;
    } else {
      // Validate the email
      const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      if (!emailRegex.test(formControl.value)) {
        return { 'EMAIL': true };
      }
    }
  }

  static isValidAgencyId(formControl: AbstractControl): { [key: string]: boolean } | null {
    // regular expression to match agency id starts with G@
    const format = /^([Gg]@)\w*/;

    if (format.test(formControl.value)) {
      return { 'invalid_agency_id': true };
    }

    return null;
  }

  static isWhitespaceValidated(formControl: AbstractControl): { [key: string]: boolean } | null {
    if (formControl.value) {
      return (formControl.value).trim().length === 0 ? { 'whitespace': true } : null;
    }
    return null;
  }

  private static parseDate(value: string): Date {
    let result;
    if (!!value && value.trim().length > 0) {
      let date = DateUtil.parseDate(value.trim());
      if (DateUtil.isValidDate(date)) {
        result = date;
      }
    }
    return result;
  }

  static postalCodeCountryCrossValidator(postalCodeControlName: string, countryFormControlName: string): ValidatorFn {

    return (formGroup: FormGroup): ValidationErrors | null => {
      let validationErrors: ValidationErrors = null;
      try {
        if (this.isPostalCodeRequired(formGroup, countryFormControlName) && this.isBlank(formGroup.get(postalCodeControlName))) {
          validationErrors = { 'postal_code_country_cross_error': true };
        }
      } catch (error) {
        PioneerValidators.LOG_SERVICE.error(error);
      }
      return validationErrors;
    };

  }

  static isPostalCodeRequired(formGroup: FormGroup, countryFormControlName: string): boolean {
    return (Constants.COUNTRIES_WITH_POSTAL_CODE.indexOf(formGroup.get(countryFormControlName).value) >= 0);
  }

  static countryStateCrossValidator(countryFormControlName: string, stateFormControlName: string): ValidatorFn {

    return (formGroup: FormGroup): ValidationErrors | null => {
      let validationErrors: ValidationErrors = null;
      if (this.isStateRequired(formGroup, countryFormControlName)) {
        if (Validators.required(formGroup.get(stateFormControlName))) {
          validationErrors = { 'country_state_cross_error': true };
        }
      }
      return validationErrors;
    };

  }

  static isStateRequired(formGroup: FormGroup, countryFormControlName: string): boolean {
    return (Constants.COUNTRIES_WITH_STATES.indexOf(formGroup.get(countryFormControlName).value) >= 0);
  }

  static oneIsRequiredValidator(...formControlNames: string[]): ValidatorFn {

    return (formGroup: FormGroup): ValidationErrors | null => {
      let validationErrors: ValidationErrors = null;
      if (formControlNames && formControlNames.findIndex(formControlName => !this.isBlank(formGroup.get(formControlName))) < 0) {
        validationErrors = { 'one_is_required_error': true };
      }
      return validationErrors;
    };

  }

  private static isBlank(formControl: AbstractControl): boolean {
    let result: boolean;
    if (Validators.required(formControl)) {
      result = true;
    } else if (!formControl.value || (formControl.value).trim().length === 0) {
      result = true;
    } else {
      result = false;
    }
    return result;
  }

  static range(min: number, max: number): ValidatorFn {
    return (formControl: AbstractControl): { [key: string]: boolean } | null => {

      let result: { [key: string]: boolean } | null;
      if (Validators.required(formControl)) {
        result = null;
      } else if (this.isInteger(formControl)) {
        result = null;
      } else if (Validators.min(min).call(null, formControl) || Validators.max(max).call(null, formControl)) {
        result = { 'range': true };
      } else {
        result = null;
      }
      return result;
    };
  }

  static isAlphaNumeric(formControl: AbstractControl): { [key: string]: boolean } | null {
    // get the failure message from Validators.required instead
    if (Validators.required(formControl)) {
      return null;
    }

    // regular expression to match 1 to 13 digits, optionally followed by a decimal and up to 2 more digits
    const format = /^[a-zA-Z0-9]+(_?|-?)[a-zA-Z0-9]+$/;

    if (!format.test(formControl.value)) {
      return { 'invalid_group_code': true };
    }

    return null;
  }

  static isEmailFormatValid(email: string): boolean {
    const emailControl = new FormControl(email, PioneerValidators.isValidEmail);
    return !emailControl.errors;
  }

  static commissionAmountAndTypeCrossValidator(calculatedPaymentWithTax: number): ValidatorFn {
    const decimalPipe = PioneerValidators.injector.get(DecimalPipe);
    return (group: AbstractControl): ValidationErrors | null => {
      const typeFormControl = group.get('type');
      const type = typeFormControl && typeFormControl.value && typeFormControl.value.key;
      if (type !== PaymentType.PAID_DIRECT) {
        return null;
      }
      if (PioneerValidators.isValidAmount(group.get('commissionAmount')) !== null) {
        return null;
      }
      const commissionAmountNumber = group.get('commissionAmount') && group.get('commissionAmount').value;
      if (!commissionAmountNumber && commissionAmountNumber !== 0) {
        return null;
      } else {
        const calculatedPaymentWithTaxFormatted = decimalPipe.transform(calculatedPaymentWithTax, '1.2-2');
        const commissionAmountString = decimalPipe.transform(commissionAmountNumber, '1.2-2');
        if (commissionAmountString !== calculatedPaymentWithTaxFormatted) {
          return { 'amount_paid_not_equals_total_calculated_payment': true };
        } else {
          return null;
        }
      }
    };
  }

  static isPropertyError(group: FormGroup): any {
    if (group.get('partyType').value === PartyType.PROPERTY.toString() &&
      (
        !group.get('propertyId').value || !group.get('chain').value || !group.get('brand').value
      )) {
      return {'propertyRequired': true};
    }
    return null;
  }

  constructor(private logService: LogService) {
    PioneerValidators.LOG_SERVICE = logService;
  }

}



// WEBPACK FOOTER //
// /home/gitlab-runner/builds/qfGQY7pgg/1/oneonyx/grouppay-group/grouppay/pioneer-web/src/app/shared/utils/pioneer.validators.ts